import equal from 'fast-deep-equal';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import {
  useGetMatterQuery,
  usePatchMatterClientMutation,
  usePatchMatterMutation,
} from '@/services/api/mattersService';
import { MatterClient } from '@/services/types/matter-client-types';

import { ClientForm } from './components/ClientForm';

interface MatterClientModalWrapperProps {
  isOpen: boolean;
  onClose: () => void;
  client: MatterClient;
  matterId: string;
  includeDateOfLoss?: boolean;
  zIndex?: number;
  nested?: boolean;
  /** Field names to highlight as needing attention (e.g., ['first_name', 'address.line1']) */
  highlightFields?: string[];
}

export const MatterClientModalWrapper: React.FC<
  MatterClientModalWrapperProps
> = ({
  isOpen,
  onClose,
  client,
  matterId,
  includeDateOfLoss = false,
  zIndex,
  nested = false,
  highlightFields = [],
}) => {
  const [clientData, setClientData] = useState<MatterClient>(client);
  const [dateOfLoss, setDateOfLoss] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);

  const { data: matter } = useGetMatterQuery(matterId);
  const [patchMatterClient, { isLoading: isSavingClient }] =
    usePatchMatterClientMutation();
  const [patchMatter, { isLoading: isSavingMatter }] = usePatchMatterMutation();

  const isSaving = isSavingClient || isSavingMatter;

  // Initialize form when client or matter changes
  useEffect(() => {
    setClientData(client);
    setDateOfLoss(matter?.date_of_loss || '');
  }, [client, matter]);

  // Track if form has changes
  useEffect(() => {
    const clientChanged = !equal(client, clientData);
    const dateOfLossChanged =
      includeDateOfLoss && dateOfLoss !== (matter?.date_of_loss || '');
    setHasChanges(clientChanged || dateOfLossChanged);
  }, [clientData, client, dateOfLoss, matter, includeDateOfLoss]);

  // Handle field changes
  const handleFieldChange = (name: string, value: unknown) => {
    setClientData((prev) => {
      const result = JSON.parse(JSON.stringify(prev));

      if (name.includes('.')) {
        const parts = name.split('.');
        const lastPart = parts.pop();
        if (!lastPart) return result;

        const targetObj = parts.reduce(
          (obj, part) => {
            if (!obj[part]) obj[part] = {};
            return obj[part] as Record<string, unknown>;
          },
          result as Record<string, unknown>,
        ) as Record<string, unknown>;

        targetObj[lastPart as string] = value;
      } else if (name === 'phone_numbers' && typeof value === 'string') {
        result[name] = JSON.parse(value);
      } else {
        result[name] = value;
      }

      return result;
    });
  };

  const handleSave = useCallback(async () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    try {
      // Update client if changed
      if (!equal(client, clientData)) {
        await patchMatterClient({
          matter_id: matterId,
          matter_client_id: client.id,
          patch: clientData,
        }).unwrap();
      }

      // Update matter date_of_loss if changed and included
      if (
        includeDateOfLoss &&
        dateOfLoss &&
        dateOfLoss !== matter?.date_of_loss
      ) {
        await patchMatter({
          matter_id: matterId,
          patch: { date_of_loss: dateOfLoss },
        }).unwrap();
      }

      toast.success('Client information updated successfully');
      onClose();
    } catch (error) {
      toast.error('Failed to update client information');
    }
  }, [
    hasChanges,
    onClose,
    client,
    clientData,
    patchMatterClient,
    matterId,
    includeDateOfLoss,
    dateOfLoss,
    matter?.date_of_loss,
    patchMatter,
  ]);

  const handleCancel = () => {
    setClientData(client);
    setDateOfLoss(matter?.date_of_loss || '');
    onClose();
  };

  // Keyboard shortcut handler
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (hasChanges && !isSaving) {
          handleSave().catch(() => {
            // Error already handled in handleSave
          });
        }
      }
    },
    [hasChanges, isSaving, handleSave],
  );

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, isOpen]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      size="3xl"
      zIndex={zIndex}
      nested={nested}
    >
      <DialogTitle>Update Client Information</DialogTitle>
      <DialogBody>
        <div className="space-y-4">
          {/* Date of Loss Field */}
          {includeDateOfLoss && (
            <Fieldset>
              <FieldGroup>
                <Field>
                  <Label htmlFor="date_of_loss">Date of Loss</Label>
                  <Input
                    id="date_of_loss"
                    type="date"
                    value={dateOfLoss}
                    onChange={(e) => setDateOfLoss(e.target.value)}
                  />
                </Field>
              </FieldGroup>
            </Fieldset>
          )}

          {/* Client Form */}
          <div className="rounded-lg border shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b">
              <h3 className="text-sm font-medium text-gray-900">
                Client Information
              </h3>
            </div>
            <div className="p-4">
              <ClientForm
                isEditing
                clientData={clientData}
                onFieldChange={handleFieldChange}
                clientId={client.id}
                matterId={matterId}
                highlightFields={highlightFields}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" outline onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
};
